import {
  useV2CreateGovernanceMutation,
  useV2UpdateGovernanceMutation,
  useV2DeleteGovernanceMutation,
  useV2RestoreGovernanceByIdMutation,
  useV2PurgeGovernanceByIdMutation,
  useV2ChangeGovernancePositionByIdMutation,
} from "@generated";
import type { GovernanceFormValues } from "../types/Governance";

const governanceAttributes = (values: GovernanceFormValues) => ({
  name: values.name,
  required: values.required,
  "calculation-type": values.calculationType,
  "fixed-hours":
    values.calculationType === "fixed_hours"
      ? values.fixedHours ?? undefined
      : undefined,
  rate:
    values.calculationType === "percent_of_total"
      ? values.rate ?? undefined
      : undefined,
  "filter-type": values.filterType,
  "filter-id": values.filterId ?? undefined,
  "allocation-method": values.allocationMethod ?? undefined,
  "assign-effort-to-service": values.assignEffortToService,
});

const governanceRelationships = (values: GovernanceFormValues) => ({
  resource: values.resourceId
    ? { data: { type: "resources", id: values.resourceId } }
    : undefined,
  phase:
    values.allocationMethod === "select_phase" && values.phaseId
      ? { data: { type: "phases", id: values.phaseId } }
      : undefined,
  "service-category":
    values.allocationMethod === "select_category" && values.sectionId
      ? {
          data: {
            type: "service-categories",
            id: values.sectionId,
          },
        }
      : undefined,
});

export const useGovernanceMutations = () => {
  const [create, createState] = useV2CreateGovernanceMutation();
  const [update, updateState] = useV2UpdateGovernanceMutation();
  const [destroy, destroyState] = useV2DeleteGovernanceMutation();
  const [restore, restoreState] = useV2RestoreGovernanceByIdMutation();
  const [purge, purgeState] = useV2PurgeGovernanceByIdMutation();
  const [move, moveState] = useV2ChangeGovernancePositionByIdMutation();

  return {
    create: (values: GovernanceFormValues) =>
      create({
        body: {
          data: {
            type: "governances",
            attributes: governanceAttributes(values),
            relationships: governanceRelationships(values),
          },
        },
      }).unwrap(),

    update: (id: string, values: GovernanceFormValues) =>
      update({
        id: id,
        body: {
          data: {
            type: "governances",
            id: id,
            attributes: governanceAttributes(values),
            relationships: governanceRelationships(values),
          },
        },
      }).unwrap(),

    destroy: (id: string) => destroy({ id: id }).unwrap(),
    restore: (id: string) => restore({ id: id }).unwrap(),
    purge: (id: string) => purge({ id: id }).unwrap(),
    move: (id: string, position: number) =>
      move({
        id: id,
        body: {
          data: {
            type: "governances",
            id: id,
            attributes: { position },
          },
        } as Parameters<typeof move>[0]["body"],
      }).unwrap(),

    states: {
      createState,
      updateState,
      destroyState,
      restoreState,
      purgeState,
      moveState,
    },
  };
};
